import { Link, Stack, Typography } from '@mui/material'
import get from 'lodash/get'
import { FieldErrors, UseFormSetValue } from 'react-hook-form'
import { DateTimePickerField, Icons } from 'utoniq-ui'

import { config } from '@/config'
import { useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'
import { convertDatePickerValueToDate, dayjs } from '@/lib/dayjs'

import { CreateItemProps } from '../ItemCreateForm'
import { ToggleInputCell } from './Commons'

type ResaleInputCellProps = {
  value: boolean
  startedAt?: string
  endedAt?: string
  setValue: UseFormSetValue<CreateItemProps>
  errors?: FieldErrors<CreateItemProps>
}

export const ResaleInputCell: Cell<ResaleInputCellProps> = ({
  value,
  startedAt,
  endedAt,
  setValue,
  errors,
}) => {
  const { t } = useTranslation('consoleItemCreate')

  return (
    <CellFrame sx={{ pb: 6 }}>
      <ToggleInputCell
        checked={value}
        onChange={(event, checked) => {
          setValue('resaleEnabled', checked, {
            shouldDirty: true,
            shouldValidate: true,
          })
          if (!checked) {
            setValue('metadata.ticket.resalePeriod.startedAt', '' as never, {
              shouldDirty: true,
              shouldValidate: true,
            })
            setValue('metadata.ticket.resalePeriod.endedAt', '' as never, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
        }}
        content={{
          title: t('resaleEnabledLabel'),
          description: (
            <Stack spacing={2}>
              <Link
                component="a"
                target="_blank"
                variant="caption"
                href={get(config, 'helpUrl', '#')}
                sx={{ alignItems: 'center', display: 'flex' }}
              >
                {t('resaleEnabledNote')}
                <Icons.Help fontSize="xSmall" color="inherit" sx={{ ml: 1 }} />
              </Link>
              <Typography variant="body2" color="text.secondary">
                {t('resaleEnabledDesc')}
              </Typography>
            </Stack>
          ),
        }}
        sx={{ pb: 4 }}
      />
      {value && (
        <Stack>
          <DateTimePickerField
            onChange={date => {
              setValue(
                'metadata.ticket.resalePeriod.startedAt',
                (convertDatePickerValueToDate(date) || '') as never,
                {
                  shouldValidate: true,
                  shouldDirty: true,
                }
              )
            }}
            value={startedAt ? dayjs(startedAt) : null}
            title={t('resalePeriodStartedAtTitle')}
            description={t('resalePeriodStartedAtDesc')}
            placeholder={t('resalePeriodStartedAtPlaceholder')}
            error={errors?.metadata?.ticket?.resalePeriod?.startedAt}
            titleProps={{
              variant: 'subtitle1',
              color: 'text.primary',
            }}
          />
          <DateTimePickerField
            onChange={date => {
              setValue(
                'metadata.ticket.resalePeriod.endedAt',
                (convertDatePickerValueToDate(date) || '') as never,
                {
                  shouldValidate: true,
                  shouldDirty: true,
                }
              )
            }}
            value={endedAt ? dayjs(endedAt) : null}
            title={t('resalePeriodEndedAtTitle')}
            placeholder={t('resalePeriodEndedAtPlaceholder')}
            error={errors?.metadata?.ticket?.resalePeriod?.endedAt}
            titleProps={{
              variant: 'subtitle1',
              color: 'text.primary',
            }}
          />
        </Stack>
      )}
    </CellFrame>
  )
}
